"use client";

import {
  motion,
  useMotionValueEvent,
  useScroll,
  useTransform,
} from "framer-motion";
import Image from "next/image";
import { useLayoutEffect, useRef, useState } from "react";

import { cn } from "@/lib/utils";

import { PmlSection } from "../profitmill-lpl/shared";
import { CASE_STUDY_IMAGE_EDGE_FADE } from "./data";
import type { CaseStudyStorySection } from "./data";

interface Stage extends CaseStudyStorySection {
  key: string;
}

const EQUITY_HIGHLIGHT_PHRASE = "without giving up equity";

/** Highlights "without giving up equity" within a blurb in bright white
 * (vs. the surrounding white/85 body text), wherever it appears — a
 * no-op if absent. */
function renderBlurbWithEquityHighlight(blurb: string) {
  const idx = blurb.toLowerCase().indexOf(EQUITY_HIGHLIGHT_PHRASE);
  if (idx === -1) return blurb;
  const before = blurb.slice(0, idx);
  const match = blurb.slice(idx, idx + EQUITY_HIGHLIGHT_PHRASE.length);
  const after = blurb.slice(idx + EQUITY_HIGHLIGHT_PHRASE.length);
  return (
    <>
      {before}
      <span className="font-semibold text-white">{match}</span>
      {after}
    </>
  );
}

interface Props {
  stages: Stage[];
}

/**
 * Step marker with its own scroll tracking (rather than a fraction of
 * the whole section) so it fills in exactly when it crosses the
 * viewport's center, matching the same offset convention as the
 * section's line-fill below. That makes the marker itself the resting
 * point of the traveling progress, instead of a separate dot trying to
 * land on top of it and disappearing behind with no clear resolution.
 */
function StageMarker({ index }: { index: number }) {
  const ref = useRef<HTMLSpanElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start center", "end center"],
  });
  const [filled, setFilled] = useState(false);
  useMotionValueEvent(scrollYProgress, "change", (v) => setFilled(v >= 0.5));

  return (
    <span
      ref={ref}
      className={cn(
        "relative z-20 mb-5 flex size-9 items-center justify-center rounded-full text-[13px] font-bold shadow-sm transition-colors duration-300",
        filled
          ? "border-2 border-[#1F2DFF] bg-[#1F2DFF] text-white"
          : "text-pulse-700 bg-white"
      )}
    >
      {index + 1}
    </span>
  );
}

/** Extra breathing room (px) between the line and each text block's edges. */
const GAP_BUFFER = 16;

/**
 * Builds a hard-edged vertical mask that hides the line/fill wherever a
 * stage's marker+text group sits, so the line visually stops just above
 * each group and resumes just below it instead of running through the
 * text.
 *
 * Stops are in PX, not %: the blue fill element's own height animates
 * from 0 to 100% of the section as the visitor scrolls, and a
 * percentage-based mask resolves against the element's *own current*
 * size by default, not the fixed section height — so the gaps would
 * drift/rescale as the fill grows, reading as a broken, fading line.
 * Pairing px stops with an explicit mask-size (set by the caller) equal
 * to the full section height keeps the mask anchored to one fixed
 * coordinate system regardless of the element's current height.
 */
function buildLineMask(
  gaps: { top: number; bottom: number }[],
  totalHeight: number
) {
  if (totalHeight <= 0 || gaps.length === 0) return undefined;
  // Use white (not black) for the "visible" stops: -webkit-mask-image
  // defaults to luminance masking (white = shown, black = hidden) while
  // unprefixed mask-image defaults to alpha masking (opaque = shown).
  // White is opaque AND full-luminance, so it reads as "shown" under
  // either interpretation; "transparent" is luminance 0 AND alpha 0, so
  // it reads as "hidden" under either.
  const stops: string[] = ["white 0px"];
  for (const { top, bottom } of gaps) {
    const start = Math.max(0, top);
    const end = Math.min(totalHeight, bottom);
    if (end <= start) continue;
    stops.push(
      `white ${start}px`,
      `transparent ${start}px`,
      `transparent ${end}px`,
      `white ${end}px`
    );
  }
  stops.push(`white ${totalHeight}px`);
  return `linear-gradient(to bottom, ${stops.join(", ")})`;
}

/**
 * Scroll-driven Problem -> Solution -> Impact narrative: a single
 * centered column (no side-by-side quote card, which tested as too
 * much to track while skimming). A feint vertical line runs behind
 * the stages and fills blue as the visitor scrolls through them, with
 * numbered step markers that fill solid blue as the line reaches them,
 * so the long gaps between stages read as progress rather than dead
 * space. The line is masked to stop above each stage's marker+text
 * group and resume below it, rather than running through the words.
 */
export function CaseStudyScrollStory({ stages }: Props) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const groupRefs = useRef<(HTMLDivElement | null)[]>([]);
  // Absolute page-scroll positions (not viewport-relative keywords) for
  // where this fill starts and finishes. Driving the fill off raw
  // scrollY against measured page positions — instead of an
  // offset:["start center","end start"] pair on sectionRef — makes the
  // finish point a plain number the quote's own continuation line (a
  // separate component below) can read and match exactly, and makes it
  // easy to tune independent of framer-motion's viewport-keyword system.
  const [scrollRange, setScrollRange] = useState({ start: 0, end: 1 });
  const { scrollY } = useScroll();
  const fillHeight = useTransform(
    scrollY,
    [scrollRange.start, scrollRange.end],
    ["0%", "100%"]
  );
  const firstStageHasBackground = Boolean(stages[0]?.backgroundSrc);
  const lastStageHasBackground = Boolean(
    stages[stages.length - 1]?.backgroundSrc
  );
  const [lineMask, setLineMask] = useState<
    { image: string; height: number } | undefined
  >(undefined);

  useLayoutEffect(() => {
    const measure = () => {
      const sectionEl = sectionRef.current;
      if (!sectionEl) return;
      const sectionRect = sectionEl.getBoundingClientRect();
      const gaps = groupRefs.current
        .filter((el): el is HTMLDivElement => el !== null)
        .map((el) => {
          const rect = el.getBoundingClientRect();
          return {
            top: rect.top - sectionRect.top - GAP_BUFFER,
            bottom: rect.bottom - sectionRect.top + GAP_BUFFER,
          };
        });
      const image = buildLineMask(gaps, sectionRect.height);
      setLineMask(image ? { image, height: sectionRect.height } : undefined);

      // Starts filling once the section's top reaches the viewport's
      // center, finishes once its bottom (the boundary with the quote
      // section right below, flush against it) *also* reaches center —
      // not the much later point where it'd cross the very top of the
      // viewport. That earlier finish keeps the hand-off to the quote's
      // continuation line inside a comfortable, still-visible scroll
      // range, instead of requiring the entire timeline to have
      // scrolled completely out of view first.
      const halfViewport = window.innerHeight / 2;
      setScrollRange({
        start: sectionRect.top + window.scrollY - halfViewport,
        end: sectionRect.bottom + window.scrollY - halfViewport,
      });
    };

    measure();
    window.addEventListener("resize", measure);
    const observer = new ResizeObserver(measure);
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => {
      window.removeEventListener("resize", measure);
      observer.disconnect();
    };
  }, [stages]);

  const maskStyle = lineMask
    ? {
        maskImage: lineMask.image,
        WebkitMaskImage: lineMask.image,
        maskSize: `100% ${lineMask.height}px`,
        WebkitMaskSize: `100% ${lineMask.height}px`,
        maskPosition: "top",
        WebkitMaskPosition: "top",
        maskRepeat: "no-repeat",
        WebkitMaskRepeat: "no-repeat",
      }
    : undefined;

  return (
    <PmlSection
      className={cn(
        "bg-grey-50",
        firstStageHasBackground && "pt-0 md:pt-0",
        lastStageHasBackground && "pb-0 md:pb-0"
      )}
    >
      <div ref={sectionRef} className="relative mx-auto max-w-[680px]">
        <div
          className="bg-grey-200 absolute top-0 bottom-0 left-1/2 z-10 w-0.5 -translate-x-1/2"
          style={maskStyle}
          aria-hidden="true"
        />
        <motion.div
          className="absolute top-0 left-1/2 z-10 w-0.5 -translate-x-1/2 bg-[#1F2DFF] shadow-[0_0_6px_rgba(31,45,255,0.8)]"
          style={{ height: fillHeight, ...maskStyle }}
          aria-hidden="true"
        />

        <div className="flex flex-col">
          {stages.map((stage, i) => (
            <div
              key={stage.key}
              className="relative flex min-h-[55vh] flex-col items-center justify-center text-center lg:min-h-[65vh]"
            >
              {stage.backgroundSrc && (
                <div
                  className="absolute inset-y-0 left-1/2 z-0 w-screen -translate-x-1/2"
                  aria-hidden="true"
                >
                  <Image
                    src={stage.backgroundSrc}
                    alt=""
                    fill
                    sizes="100vw"
                    className="object-cover"
                  />
                  <div
                    className="absolute inset-0"
                    style={{ background: CASE_STUDY_IMAGE_EDGE_FADE }}
                  />
                </div>
              )}
              <div
                ref={(el) => {
                  groupRefs.current[i] = el;
                }}
                className="relative z-20 flex flex-col items-center px-4 py-2"
              >
                <StageMarker index={i} />
                <motion.div
                  initial={{ opacity: 0.25, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ amount: 0.6, once: false }}
                  transition={{ duration: 0.4, ease: "easeOut" }}
                >
                  <p
                    className={cn(
                      "text-body-xs mb-3 font-bold tracking-wide uppercase",
                      stage.backgroundSrc ? "text-white" : "text-pulse-700"
                    )}
                  >
                    {stage.kicker}
                  </p>
                  <h3
                    className={cn(
                      "text-heading-32 lg:text-display-40 mb-5",
                      stage.backgroundSrc ? "text-white" : "text-black"
                    )}
                  >
                    {stage.title}
                  </h3>
                  <p
                    className={cn(
                      "text-body-16 lg:text-body-md mx-auto max-w-[560px]",
                      stage.backgroundSrc ? "text-white/85" : "text-grey-700"
                    )}
                  >
                    {renderBlurbWithEquityHighlight(stage.blurb)}
                  </p>
                </motion.div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </PmlSection>
  );
}
